import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnInit,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { KeyboardEventCode } from '@recruitee/common/keyboard';
import { isEmpty } from '@recruitee/utils';

import { KeyCode } from '../../core/keycode';

export const CODE_INPUT_VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  // eslint-disable-next-line @angular-eslint/no-forward-ref
  useExisting: forwardRef(() => CodeInputComponent),
  multi: true,
};

@Component({
  selector: 'rt-code-input',
  templateUrl: './code-input.component.html',
  styleUrls: ['./code-input.component.less'],
  providers: [CODE_INPUT_VALUE_ACCESSOR],
  imports: [],
})
export class CodeInputComponent implements OnInit, ControlValueAccessor {
  @ViewChildren('input') public inputsList: QueryList<ElementRef>;

  @Input() public codeLength: number = 6;
  @Input() public error: { error: string[] } | null;

  @Output() public codeCompleted: EventEmitter<string> = new EventEmitter();
  @Output() public clearError: EventEmitter<void> = new EventEmitter();

  public placeholders: number[];

  private inputs: HTMLInputElement[] = [];
  private value: string;

  constructor(private cdRef: ChangeDetectorRef) {}

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public onTouched = () => {};

  public ngOnInit(): void {
    this.placeholders = new Array(this.codeLength).fill(1);
  }

  public ngAfterViewInit(): void {
    this.inputsList.forEach(item => {
      this.inputs.push(item.nativeElement);
    });
  }

  public registerOnChange(fn: any): void {
    this.codeCompleted.subscribe(fn);
  }

  public registerOnTouched(fn: () => {}): void {
    this.onTouched = fn;
  }

  public writeValue(value: string): void {
    this.value = value;
    this.cdRef.detectChanges();
  }

  public onClick(index: number): void {
    if (!this.error) return;

    this.clearError.emit();

    this.inputs.forEach((input: HTMLInputElement) => {
      this.setInputValue(input, '');
    });

    if (index === 0) return;
    this.inputs[0].focus();
  }

  public onInput(e: Partial<InputEvent>, i: number): void {
    const target = <HTMLInputElement>e.target;
    const value = e.data || target.value;

    const isEmptyValue = isEmpty(value);
    if (isEmptyValue) return;

    const values = value.toString().trim().split('');

    values.forEach((value: string) => {
      if (i > this.codeLength - 1) return;
      this.setInputValue(this.inputs[i], value);
    });

    this.emitChanges();

    const next = i + values.length;
    if (next > this.codeLength - 1) {
      target.blur();
      return;
    }

    this.inputs[next].focus();
  }

  public async onKeydown(e: KeyboardEvent, i: number): Promise<void> {
    const target = <HTMLInputElement>e.target!;
    const isTargetEmpty = isEmpty(target.value);
    const prev = i - 1;

    const isBackspaceKey = await this.isBackspaceKey(e);

    if (!isBackspaceKey) return;

    e.preventDefault();

    this.setInputValue(target, '');
    if (!isTargetEmpty) {
      this.emitChanges();
    }

    if (prev < 0) return;

    if (isTargetEmpty) {
      this.inputs[prev].focus();
    }
  }

  public onPaste(e: ClipboardEvent, i: number): void {
    e.preventDefault();
    e.stopPropagation();

    const data = e.clipboardData ? e.clipboardData.getData('text').trim() : undefined;

    if (isEmpty(data)) return;

    const values = (<string>(<unknown>data)).split('');
    let valIndex = 0;

    this.inputs.forEach((input: HTMLInputElement) => {
      if (valIndex === values.length) return;

      const val = values[valIndex];

      if (!this.canInputValue(val)) {
        this.setInputValue(input, '');
        return;
      }

      this.setInputValue(input, val.toString());
      valIndex++;
    });

    this.inputs[i].blur();
    this.emitChanges();
  }

  public trackByIndex(index: number): number {
    return index;
  }

  private isBackspaceKey(e: KeyboardEvent): Promise<boolean> {
    const isBackspace = e?.key === KeyboardEventCode.Backspace || e?.keyCode === KeyCode.Backspace;

    if (isBackspace) {
      return Promise.resolve(true);
    }

    return new Promise<boolean>(resolve => {
      setTimeout(() => {
        const input = <HTMLInputElement>e.target;
        resolve(input?.selectionStart === 0);
      });
    });
  }

  private getCurrentFilledCode(): string {
    const code = this.inputs
      .filter(input => !isEmpty(input.value))
      .reduce((acc, input) => acc + input.value, '');

    return code;
  }

  private setInputValue(input: HTMLInputElement, value: string): void {
    input.value = isEmpty(value) ? '' : value;
  }

  private emitChanges(): void {
    const code = this.getCurrentFilledCode();

    if (code.length >= this.codeLength) {
      this.codeCompleted.emit(code);
    }
  }

  private canInputValue(value: string): boolean {
    if (isEmpty(value)) {
      return false;
    }

    const isDigitsValue = /^[0-9]+$/.test(value);
    return isDigitsValue;
  }
}
